


































































































































































import Vue from 'vue';
import { api, BANK_API_SUB_URLS } from '@/api';
import { numeric, required, sameAs, maxLength } from 'vuelidate/lib/validators';
import { req } from 'vuelidate/lib/validators/common';
import { bankAccountNumber, exactLength } from '@/validations';
import InputMaskTypes from '@/directives/input-mask/input-mask-types';
import {
  CloseFullscreenWrapper,
  FormValidation,
  OpenHelp,
  TriggerRefreshError,
} from '@/mixins';
import {
  FullscreenWrapper,
  FullscreenSuccess,
  FullscreenError,
  FullscreenLoading,
} from '@/components/fullscreen-wrapper-components';
import { ExtendMixins } from '@/services';
import {
  AddBankAccountFormModel,
  MemberAccountResponseModel,
  MemberBankAccountResponseModel, SelectOption,
  UserModel,
} from '@/@types/member';

interface Data {
  isLoading: boolean;
  showSuccessMessage: boolean;
  successMessage: string;
  showErrorMessage: boolean;
  errorMessage: string;
  errorMessageTokenInvalid: string;
  confirmBeforeClose: boolean;
  showRoutingNumberError: boolean;
  checkingRoutingNumber: boolean;
  routingNumberValidated: boolean;
  bankAccountModel: AddBankAccountFormModel;
}

export default ExtendMixins(
  CloseFullscreenWrapper,
  FormValidation,
  OpenHelp,
  TriggerRefreshError,
).extend({
  name: 'AddBankAccount',
  props: {
    params: {
      type: Object as () => any,
      required: true,
    },
  },
  data(): Data {
    return {
      isLoading: false,
      showSuccessMessage: false,
      successMessage: this.params.successMessage,
      showErrorMessage: false,
      errorMessage: this.params.errorMessage,
      errorMessageTokenInvalid: this.params.errorMessageTokenInvalid,
      confirmBeforeClose: true,
      showRoutingNumberError: false,
      checkingRoutingNumber: false,
      routingNumberValidated: false,
      bankAccountModel: {
        token: '',
        accountNumber: '',
        confirmAccountNumber: '',
        routingNumber: '',
        accountType: 'Checking',
        bankName: '',
        addBankAccountTerms: false,
      },
    };
  },
  beforeMount() {
    this.constructShowErrorModel('bankAccountModel');
  },
  validations: {
    bankAccountModel: {
      token: {
        required,
      },
      accountNumber: {
        required,
        bankAccountNumber,
        maxLength: maxLength(17),
      },
      confirmAccountNumber: {
        required,
        sameAs: sameAs('accountNumber'),
      },
      routingNumber: {
        required,
        exactLength: exactLength(9),
        numeric,
        validRoutingNumber(value): boolean {
          return !req(value) || !this.showRoutingNumberError;
        },
      },
      accountType: {
        required,
      },
      bankName: {
        required,
      },
      addBankAccountTerms: {
        required,
      },
    },
  },
  computed: {
    user(): UserModel {
      return this.$store.getters.user;
    },
    accountTypeOptions(): SelectOption[] {
      return [
        {
          value: 'Checking',
          text: this.$options.filters.t([
            'add-bank-account_label_checking',
            'bank-accounts_label_checking',
            'global_checking',
          ]),
        },
        {
          value: 'Savings',
          text: this.$options.filters.t([
            'add-bank-account_label_savings',
            'bank-accounts_label_savings',
            'global_savings',
          ]),
        },
      ];
    },
    bankAccounts(): MemberBankAccountResponseModel[] {
      return this.$store.getters.bankAccounts;
    },
    accounts(): MemberAccountResponseModel[] {
      return this.$store.getters.accounts;
    },
    hasActiveHSA(): boolean {
      return !this.accounts.length
        ? false
        : !!this.accounts.filter((account) => {
            return account.productType === 'HSA' && account.isActive;
          });
    },
    useForClaims(): boolean {
      return !(this.bankAccounts.length && this.hasActiveHSA);
    },

    //      VALIDATIONS
    tokenErrors(): string[] {
      const formField = 'bankAccountModel.token';
      return this.FormValidationMixin.constructErrors(formField, [
        this.FormValidationMixin.validateField(
          formField,
          [
            'global_form-error_token_required',
            'global_form-error_default_required',
          ],
          'required',
        ),
      ]);
    },
    accountNumberErrors(): string[] {
      const formField = 'bankAccountModel.accountNumber';
      return this.FormValidationMixin.constructErrors(formField, [
        this.FormValidationMixin.validateField(
          formField,
          [
            'add-bank-account_form-error_account-number_required',
            'global_form-error_account-number_required',
            'global_form-error_default_required',
          ],
          'required',
        ),
        this.FormValidationMixin.validateField(
          formField,
          [
            'add-bank-account_form-error_account-number_invalid-format',
            'global_form-error_default_invalid-format',
          ],
          'bankAccountNumber',
        ),
        this.FormValidationMixin.validateField(
          formField,
          'add-bank-account_form-error_account-number_length',
          'maxLength',
          { key: 'maxLength', val: 'max' },
        ),
      ]);
    },
    confirmAccountNumberErrors(): string[] {
      const formField = 'bankAccountModel.confirmAccountNumber';
      return this.FormValidationMixin.constructErrors(formField, [
        this.FormValidationMixin.validateField(
          formField,
          [
            'add-bank-account_form-error_confirm-account-number_required',
            'global_form-error_confirm-account-number_required',
            'global_form-error_default_required',
          ],
          'required',
        ),
        this.FormValidationMixin.validateField(
          formField,
          ['add-bank-account_form-error_confirm-account-number_same-as'],
          'sameAs',
        ),
      ]);
    },
    routingNumberErrors(): string[] {
      const formField = 'bankAccountModel.routingNumber';
      return this.FormValidationMixin.constructErrors(formField, [
        this.FormValidationMixin.validateField(
          formField,
          [
            'add-bank-account_form-error_routing-number_required',
            'global_form-error_routing-number_required',
            'global_form-error_default_required',
          ],
          'required',
        ),
        this.FormValidationMixin.validateField(
          formField,
          ['add-bank-account_form-error_routing-number_length'],
          'exactLength',
        ),
        this.FormValidationMixin.validateField(
          formField,
          [
            'add-bank-account_form-error_routing-number_invalid',
            'global_form-error_default_invalid-format',
          ],
          'numeric',
        ),
        this.FormValidationMixin.validateField(
          formField,
          [
            'add-bank-account_form-error_routing-number_invalid-format',
            'global_form-error_default_invalid-format',
          ],
          'validRoutingNumber',
        ),
      ]);
    },
    accountTypeErrors(): string[] {
      const formField = 'bankAccountModel.accountType';
      return this.FormValidationMixin.constructErrors(formField, [
        this.FormValidationMixin.validateField(
          formField,
          [
            'add-bank-account_form-error_account-type_required',
            'global_form-error_account-type_required',
            'global_form-error_default_required',
          ],
          'required',
        ),
      ]);
    },
    bankNameErrors(): string[] {
      const formField = 'bankAccountModel.bankName';
      return this.FormValidationMixin.constructErrors(formField, [
        this.FormValidationMixin.validateField(
          formField,
          [
            'add-bank-account_form-error_bank-name_required',
            'global_form-error_bank-name_required',
            'global_form-error_default_required',
          ],
          'required',
        ),
      ]);
    },
    addBankAccountTermsErrors(): string[] {
      const formField = 'bankAccountModel.addBankAccountTerms';
      return this.FormValidationMixin.constructErrors(formField, [
        this.FormValidationMixin.validateField(
          formField,
          [
            'add-bank-account_form-error_terms-and-conditions_required',
            'global_form-error_terms-and-conditions_required',
            'global_form-error_default_required',
          ],
          'required',
        ),
      ]);
    },
  },
  methods: {
    tryAgain(): void {
      if (this.errorMessage === this.errorMessageTokenInvalid) {
        // We need user to start over and request a new token
        this.closeFullscreenWrapper();
        return;
      }
      this.showErrorMessage = false;
      this.confirmBeforeClose = true;
    },
    deconstructAccountNumber(): void {
      this.bankAccountModel.accountNumber = InputMaskTypes.bankAccountNumber.deconstruct(
        this.bankAccountModel.accountNumber,
      );
    },
    validateRoutingNumber(): void {
      this.showRoutingNumberError = false;
      if (this.$v.bankAccountModel.routingNumber.$dirty) {
        if (!this.$v.bankAccountModel.routingNumber.$invalid) {
          this.checkingRoutingNumber = true;
          this.$store
            .dispatch(
              'validateRoutingNumber',
              this.bankAccountModel.routingNumber,
            )
            .then((data) => {
              this.checkingRoutingNumber = false;
              this.routingNumberValidated = true;
              this.showRoutingNumberError = false;
              this.bankAccountModel.routingNumber = data.routingNumber;
              this.bankAccountModel.bankName = data.bankName;
              this.triggerBlur('bankAccountModel.routingNumber');
            })
            .catch(() => {
              this.showRoutingNumberError = true;
              this.routingNumberValidated = false;
              this.checkingRoutingNumber = false;
              this.triggerBlur('bankAccountModel.routingNumber');
            });
        } else {
          this.bankAccountModel.bankName = '';
          this.triggerBlur('bankAccountModel.routingNumber');
        }
      } else {
        this.triggerBlur('bankAccountModel.routingNumber');
      }
    },
    addBankAccount(): void {
      const payload = {
        callingApplication: 'Member',
        bankName: this.bankAccountModel.bankName,
        routingNumber: this.bankAccountModel.routingNumber,
        accountNumber: this.bankAccountModel.accountNumber,
        accountType: this.bankAccountModel.accountType,
        useForClaims: this.useForClaims,
        useForContributions: true,
      };
      this.isLoading = true;
      api
        .post(
          `${BANK_API_SUB_URLS.members}/${this.user.memberId}/bank-accounts`,
          payload,
          {
            headers: {
              'x-token': this.bankAccountModel.token,
            },
          },
        )
        .then(() => {
          this.refreshBankingInformation();
        })
        .catch((error) => {
          if (error.status === 403) {
            this.errorMessage = this.errorMessageTokenInvalid;
          } else {
            this.errorMessage = this.params.errorMessage;
          }

          this.confirmBeforeClose = false;
          this.showErrorMessage = true;
          this.isLoading = false;
        });
    },
    refreshBankingInformation(): void {
      this.$store
        .dispatch('getBankingInformation')
        .then(() => {
          this.confirmBeforeClose = false;
          this.showSuccessMessage = true;
          this.isLoading = false;
        })
        .catch(() => {
          this.isLoading = false;
          this.triggerRefreshError({
            message: [
              'add-bank-account_text_refresh-data-error-message',
              'global_connection-error',
            ],
          });
        });
    },
  },
  components: {
    FullscreenWrapper,
    FullscreenSuccess,
    FullscreenError,
    FullscreenLoading,
  },
});
